import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, switchMap } from 'rxjs';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';
import { PurchaseOrderWorkflowDirective } from '@features/feature-workflow/directives/purchase-order-workflow.directive';
import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';

import { ChecklistToggleStore } from '@models/checklist-toggle/checklist-toggle.store';

import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { EventType, GqlService } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { WorkflowStepType } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { EXECUTABLE_BY_ROUTE_PARAM } from '@shared/utils/route.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    VendorWorkflowDirective,
    SiteWorkflowDirective,
    InvoiceWorkflowDirective,
    PurchaseOrderWorkflowDirective,
  ],
  imports: [ButtonGroupComponent],
  selector: 'aux-quick-add-actions',
  styleUrls: ['./quick-add-actions.component.scss'],
  templateUrl: './quick-add-actions.component.html',
})
export class QuickAddActionsComponent {
  private checklistToggleStore = inject(ChecklistToggleStore);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private invoiceCtx = inject(InvoiceWorkflowDirective);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private purchaseOrderCtx = inject(PurchaseOrderWorkflowDirective);
  private router = inject(Router);
  private siteCtx = inject(SiteWorkflowDirective);
  private trialsQuery = inject(TrialsQuery);
  private vendorCtx = inject(VendorWorkflowDirective);
  private vendorQuery = inject(OrganizationQuery);
  private workflowQuery = inject(WorkflowQuery);

  private anyVendorHasBudget = signal(false);

  private readonly noVendorTooltip = computed(() => {
    const vendors = this.vendorQuery.allVendors();

    if (!vendors.length) {
      return this.atLeastOneVendorRequiredMessage;
    }

    return '';
  });
  private readonly addPODisabledTooltip = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(...this.purchaseOrderCtx.addPoActionTooltips());
    tooltips.push(this.noVendorTooltip());

    return tooltips.filter(Boolean);
  });
  private readonly addSiteDisabledTooltip = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(...this.siteCtx.addSiteActionTooltips());
    tooltips.push(this.noVendorTooltip());

    return tooltips.filter(Boolean);
  });
  private readonly invoiceDisabledTooltip = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(this.invoiceCtx.invoiceAddActionTooltip());

    tooltips.push(this.noVendorTooltip());

    return tooltips.filter(Boolean);
  });

  isPatientTrackerLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  private atLeastOneVendorWithBudgetMessage =
    'At least one vendor contract with a vendor budget must be added to perform this action';

  private readonly uploadEDCDataDisabledTooltip = computed(() => {
    const tooltips: string[] = [];

    tooltips.push(this.isPatientTrackerLocked() ? MessagesConstants.EDC_DATA_LOCKED : '');
    tooltips.push(!this.anyVendorHasBudget() ? this.atLeastOneVendorWithBudgetMessage : '');

    return tooltips.filter(Boolean);
  });

  // eslint-disable-next-line max-lines-per-function
  options: Signal<AuxButtonGroup[]> = computed(() => [
    {
      disabled: this.isPatientTrackerLocked() || !this.anyVendorHasBudget(),
      disabledTooltips: this.uploadEDCDataDisabledTooltip(),
      name: 'EDC Data',
      onClick: () => {
        this.checklistToggleStore.update({
          GatherPatients: true,
        });
        this.navigateTo(`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CHECKLIST}`);
      },
      pendoId: 'quick-add-edc',
      show:
        this.launchDarklyService.$select((flags) => flags.checklist_tab)() &&
        !this.trialsQuery.isCurrentTrialCMC(this.mainQuery.mainState().trialKey)(),
    },
    {
      disabled: this.siteCtx.disabledAddSiteUI() || !this.vendorQuery.allVendorsLength(),
      disabledTooltips: this.addSiteDisabledTooltip(),
      disabledTooltipsTitle: 'Site cannot be added:',
      name: 'Site',
      onClick: () =>
        this.navigateTo(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}`
        ),
      pendoId: 'quick-add-site',
      show:
        this.launchDarklyService.$select((flags) => flags.tab_investigator_sites)() &&
        this.launchDarklyService.$select((flags) => flags.nav_investigator)() &&
        !this.trialsQuery.isCurrentTrialCMC(this.mainQuery.mainState().trialKey)(),
    },
    {
      disabled: !this.anyVendorHasBudget(),
      disabledTooltip: !this.anyVendorHasBudget() ? this.atLeastOneVendorWithBudgetMessage : '',
      name: 'Vendor Estimate Support',
      onClick: () => {
        this.navigateTo(`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`);
      },
      pendoId: 'quick-add-vendor-estimate-support',
      show: this.launchDarklyService.$select((flags) => flags.tab_in_month_adjustments)(),
    },
    {
      disabled: !this.vendorQuery.allVendorsLength(),
      disabledTooltip: this.noVendorTooltip(),
      name: 'Change Order',
      onClick: () =>
        this.navigateTo(`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`),
      pendoId: 'quick-add-co',
      show:
        this.launchDarklyService.$select((flags) => flags.tab_change_order_review)() &&
        this.launchDarklyService.$select((flags) => flags.nav_budget)(),
    },
    {
      disabled: this.invoiceCtx.disabledAddInvoiceUI() || !this.vendorQuery.allVendorsLength(),
      disabledTooltips: this.invoiceDisabledTooltip(),
      name: 'Invoice',
      onClick: () =>
        this.navigateTo(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`
        ),
      pendoId: 'quick-add-invoice',
      show: this.launchDarklyService.$select((flags) => flags.tab_invoices)(),
    },
    {
      disabled: this.purchaseOrderCtx.disabledAddPoUI() || !this.vendorQuery.allVendorsLength(),
      disabledTooltips: this.addPODisabledTooltip(),
      disabledTooltipsTitle: 'Purchase Order cannot be added:',
      name: 'PO',
      onClick: () =>
        this.navigateTo(`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PURCHASE_ORDERS}`),
      pendoId: 'quick-add-po',
      show:
        this.launchDarklyService.$select((flags) => flags.tab_vendors)() &&
        this.launchDarklyService.$select((flags) => flags.tab_purchase_orders)(),
    },
    {
      disabled: this.vendorCtx.disabledUI(),
      disabledTooltips: this.vendorCtx.vendorActionTooltips(),
      disabledTooltipsTitle: 'Vendor Contract cannot be added:',
      name: 'Vendor Contract',
      onClick: () =>
        this.navigateTo(`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.VENDORS}`),
      pendoId: 'quick-add-vendor-contract',
      show: this.launchDarklyService.$select((flags) => flags.tab_vendors)(),
    },
  ]);

  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  isClosingPanelEnabled = this.launchDarklyService.$select(
    (flags) => flags.closing_checklist_toolbar
  );

  private atLeastOneVendorRequiredMessage =
    'At least one vendor contract must be added to perform this action';
  private checkVendorBudgetExist$ = new BehaviorSubject<void>(undefined);

  constructor() {
    combineLatest([this.mainQuery.select('trialKey'), this.checkVendorBudgetExist$])
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => this.gqlService.listOrganizationWithBudgetVersions$(false))
      )
      .subscribe(({ data }) => {
        this.anyVendorHasBudget.set(
          !!data?.some((vendor) => !!vendor.current_budget_versions.length)
        );
      });

    [EventType.BUDGET_TEMPLATE_UPLOADED, EventType.INLINE_EDIT_BUDGET_ACTIVITIES].forEach(
      (event) => {
        this.eventService
          .select$(event)
          .pipe(takeUntilDestroyed())
          .subscribe(() => {
            this.checkVendorBudgetExist$.next();
          });
      }
    );
  }

  navigateTo(link: string) {
    void this.router.navigate([link], {
      queryParams: { [EXECUTABLE_BY_ROUTE_PARAM]: true },
    });
  }
}
